import { getAPI, type UserAPI } from '@sydle/ui-api';
import { type UIAppConfig } from '@sydle/ui-app';
import { getConfig, getRouter } from '@sydle/ui-utils';
import { type BlogConfig, type LinkAttributes } from '../types/blog';

export type UtmTagVariables = {
  id?: string;
  identifier?: string;
};

export type PageType = 'home' | 'search' | 'category' | 'tag' | 'post' | 'default';

export function getBaseURL(): string {
  return getConfig<BlogConfig>('sydle-ui-blog')?.baseUrl || window.location.origin;
}

export function getLangPath(): string {
  const configByLanguage = getConfig<BlogConfig>('sydle-ui-blog')?.configByLanguage;
  const language = getAPI<UserAPI>('userAPI')?.getCurrentLanguage();
  const languageConfig = configByLanguage?.find((config) => config.language.code === language);
  return languageConfig?.urlPath ? `/${languageConfig?.urlPath}` : '';
}

export function getAssetsURL(path: string): string {
  return path.match(/https:\/\//) ? path : `${window.location.origin}/blog/assets${path}/`;
}

export async function setLinkTags(links: LinkAttributes[]): Promise<void> {
  document
    .querySelectorAll('link[rel="alternate"]:not([type="application/rss+xml"]), link[rel="canonical"]')
    .forEach((link) => link.remove());
  document.head.prepend(...links.map((link) => Object.assign(document.createElement('link'), link)));
}

export function getWindowConfig(identifier: string, key?: string): { [key: string]: any } | string {
  if (key) {
    return (window as any)?.[identifier]?.[key];
  }

  return (window as any)?.[identifier];
}

export function refreshHead(tags: any, template: string, title?: string, isDraft?: boolean): void {
  const head = document.querySelector('head');
  head?.querySelectorAll('meta[name^=description]').forEach((el) => {
    el.remove();
  });
  head?.querySelectorAll('meta[name^=googlebot]').forEach((el) => {
    el.remove();
  });
  head?.querySelectorAll('meta[property^=og]').forEach((el) => {
    el.remove();
  });
  head?.querySelectorAll('meta[name^=twitter]').forEach((el) => {
    el.remove();
  });
  if (!isDraft) {
    head?.querySelectorAll('meta[name^=robots]').forEach((el) => {
      el.remove();
    });
  }
  head?.querySelectorAll('meta[property^=article]').forEach((el) => {
    el.remove();
  });
  head?.querySelectorAll('title').forEach((el) => {
    el.remove();
  });
  head?.querySelectorAll('script[src="//www.instagram.com/embed.js"]').forEach((el) => {
    el.remove();
  });
  tags?.head?.OGTags?.forEach((tag) => {
    const meta = document.createElement('meta');
    Object.keys(tag).forEach((key) => {
      meta.setAttribute(key, tag[key]);
    });
    document.head?.append(meta);
  });
  tags?.head?.contentTags?.forEach((tag) => {
    const meta = document.createElement('meta');
    Object.keys(tag).forEach((key) => {
      meta.setAttribute(key, tag[key]);
    });
    document.head?.append(meta);
  });
  document.title =
    (template ? template.replace('{{string}}', title || '') : '') + getConfig<UIAppConfig>('ui-app')?.titleSufix;
}

export function addUtmTagsToExternalLink(event: MouseEvent): void {
  const composedPathNodes = event.composedPath();
  const anchors = composedPathNodes.filter((node) => (node as any).nodeName === 'A' && (node as any).href);

  const location = getRouter()?.location;
  const routeName = location?.route?.name;
  const params = location?.params;

  anchors.forEach((anchor) => {
    // Check if it's external URL
    const internalUrlRegex = new RegExp(`^(${window.location.origin})?/(\\w{2}(-\\w{2})?/)?blog`);
    const anchorHref = (anchor as HTMLAnchorElement).href;
    if (internalUrlRegex.test(anchorHref)) return;

    // Necessário para não corromper url do download do conteúdo rico com as utm tags
    if (anchorHref.startsWith('blob:')) return;

    // URL is external. Get the utm tags
    const utmTagVariables: UtmTagVariables = {};

    switch (routeName) {
      case 'post':
        utmTagVariables.id = params?.postId?.toString();
        utmTagVariables.identifier = params?.postIdentifier?.toString();
        break;

      case 'category':
        utmTagVariables.id = params?.categoryId?.toString();
        utmTagVariables.identifier = params?.categoryIdentifier?.toString();
        break;
      case 'tag':
        utmTagVariables.id = params?.tagId?.toString();
        utmTagVariables.identifier = params?.tagIdentifier?.toString();
        break;
    }

    (anchor as HTMLAnchorElement).href = getUrlWithUtmTags(
      (anchor as HTMLAnchorElement).href,
      routeName as PageType,
      utmTagVariables,
    );
  });
}

function getUrlWithUtmTags(baseUrl: string, pageType: PageType, variables: UtmTagVariables): string {
  const config = getConfig<BlogConfig>('sydle-ui-blog');
  const title = config?.name;
  const lang = getAPI<UserAPI>('userAPI')?.getCurrentLanguage();
  const description = (lang && config?.description?.[lang]) || config?.description;

  const utmTagsConfigs = config?.trackingParamsConfigs_utmTags?.find((config) => config.pageTypes.includes(pageType));
  if (!utmTagsConfigs) return baseUrl;

  const utmTags = {};
  utmTagsConfigs.utmTags.forEach((tag) => {
    if (!tag.value) return;
    const compiledTag = tag.value
      .replace('{{id}}', variables.id || '')
      .replace('{{identifier}}', variables.identifier || '')
      .replace('{{title}}', title || '')
      .replace('{{description}}', description || '');

    if (compiledTag) utmTags[tag.key] = compiledTag;
  });

  const url = new URL(baseUrl);
  const params = new URLSearchParams(url.search);
  Object.keys(utmTags).forEach((key) => {
    params.set(key, utmTags[key]);
  });
  url.search = params.toString();
  return url.toString();
}
